import React, { useState } from 'react';
import { MediaItem } from '../../../api/structs/media';
import { MediaLibraryItem } from '../../../api/media';
import { constructMediaUrl } from '../../../api/helpers';
import { VideoCapabilities, excluded, supportsLastFrameOf } from '../../../api/videoCapabilities';
import MediaLibraryPickerModal from '../../modals/MediaLibraryPickerModal';
import MediaThumbImage from '../MediaThumbImage';
import { getMediaThumbUrl } from './MediaEditorSection.helpers';
import ReferenceInputsEditor from './ReferenceInputsEditor';
import {
  FrameBinding,
  FrameSlot,
  ReferenceFrameState,
  bindingFromMediaItem,
  resolveFrameBindingItem,
} from './referenceFrames';
import {
  ReferenceInputState,
  countReferenceInputs,
  emptyReferenceInputs,
} from './referenceInputs';

interface ReferenceFramesEditorProps {
  state: ReferenceFrameState;
  onChange: (next: ReferenceFrameState) => void;
  /** Stills from the same scene, offered as one-click bindings. */
  candidates: MediaItem[];
  /** The model's parsed video capabilities — frame support/positions drive
   *  this editor's first/last slots and messaging. */
  caps: VideoCapabilities;
  /** False while the model constraints are still loading. */
  capabilityKnown: boolean;
  /**
   * Regenerate the still behind a slot. OPTIONAL: a host that already offers
   * regeneration elsewhere omits it and the per-slot micro-button disappears
   * rather than duplicating it. The scene-level modal's Frames tab does exactly
   * that — it mounts a full RegenerateForm per frame it can resolve to a media
   * item, and mounts this editor only to view/swap/clear the frames it CANNOT.
   * Offering a second, weaker regenerate button there would also be a lie about
   * which still it hits: this editor's `source` resolves against `candidates`
   * by URL heuristics, while the modal resolves by media id.
   */
  onRegenerateImage?: (item: MediaItem) => void;
  /** Media id currently being regenerated, if any. */
  busyMediaId?: string | null;
  /** Bumped after a regenerate so the in-place overwrite repaints. */
  refreshToken?: number;
  /**
   * Reference INPUTS for the same generation (see ./referenceInputs). Both
   * halves or neither: the host owns the state, because it is the host that
   * submits it. Omitted, no reference rows are offered at all and this editor
   * is exactly what it was before.
   */
  references?: ReferenceInputState;
  onReferencesChange?: (next: ReferenceInputState) => void;
}

const microButton =
  'font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500 hover:text-white transition-colors disabled:text-zinc-700 disabled:hover:text-zinc-700';

interface FrameSlotCardProps {
  slot: FrameSlot;
  label: string;
  binding: FrameBinding | null;
  dimmed: boolean;
  candidates: MediaItem[];
  onPick: (binding: FrameBinding | null) => void;
  onBrowse: () => void;
  onRegenerateImage?: (item: MediaItem) => void;
  busyMediaId?: string | null;
  refreshToken?: number;
}

const FrameSlotCard: React.FC<FrameSlotCardProps> = ({
  slot,
  label,
  binding,
  dimmed,
  candidates,
  onPick,
  onBrowse,
  onRegenerateImage,
  busyMediaId,
  refreshToken,
}) => {
  const source = resolveFrameBindingItem(binding, candidates);
  const busy = !!source && busyMediaId === source.id;
  const cacheBuster = refreshToken ? refreshToken : undefined;

  return (
    <div className={`flex-1 min-w-[8rem] ${dimmed ? 'opacity-40' : ''}`}>
      <div className="aspect-square w-full border border-white/10 bg-black/40 overflow-hidden">
        {binding?.url ? (
          <MediaThumbImage
            key={`${binding.url}-${refreshToken ?? 0}`}
            thumbUrl={source ? getMediaThumbUrl(source) : ''}
            originalUrl={constructMediaUrl(binding.url, cacheBuster)}
            alt={label}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-700">
            empty
          </div>
        )}
      </div>

      <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
        {label}
        {binding && (
          <span
            className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500"
            data-testid={`in-use-${slot}`}
          >
            in use
          </span>
        )}
      </p>

      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <button type="button" className={microButton} onClick={onBrowse} data-testid={`browse-${slot}`}>
          Library
        </button>
        {source && onRegenerateImage && (
          <button
            type="button"
            className={microButton}
            disabled={busy}
            onClick={() => onRegenerateImage(source)}
            data-testid={`regen-image-${slot}`}
          >
            {busy ? 'Regenerating…' : 'Regenerate image'}
          </button>
        )}
        {binding && (
          <button type="button" className={microButton} onClick={() => onPick(null)}>
            Clear
          </button>
        )}
      </div>

      {candidates.length > 0 && (
        <div className="mt-1.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">
            Swap with another scene image
          </p>
          <div className="mt-1 flex items-center gap-1.5">
          {candidates.map((item) => {
            const candidateBinding = bindingFromMediaItem(item);
            if (!candidateBinding) return null;
            const active = binding?.url === candidateBinding.url;
            return (
              <button
                key={item.id}
                type="button"
                title={item.prompt}
                aria-label={`Use scene image for ${label}`}
                aria-pressed={active}
                onClick={() => onPick(candidateBinding)}
                className={`w-7 h-7 border overflow-hidden transition-colors ${
                  active ? 'border-white' : 'border-white/10 hover:border-white/40'
                }`}
              >
                <MediaThumbImage
                  thumbUrl={getMediaThumbUrl(item)}
                  originalUrl={constructMediaUrl(candidateBinding.url)}
                  alt=""
                  className="w-full h-full"
                  imgClassName="w-full h-full object-cover"
                />
              </button>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Shows — and rebinds — the stills a video generation opens and closes on.
 *
 * The two slots are joined by a chain rule: solid while the video is chained to
 * a closing frame, broken when it is not. Clicking the rule is the on/off
 * switch, so the state of the chain is the shape of the control.
 */
const ReferenceFramesEditor: React.FC<ReferenceFramesEditorProps> = ({
  state,
  onChange,
  candidates,
  caps,
  capabilityKnown,
  onRegenerateImage,
  busyMediaId,
  refreshToken,
  references,
  onReferencesChange,
}) => {
  const [pickerSlot, setPickerSlot] = useState<FrameSlot | null>(null);
  const supportsFrameImages = caps.frames.supported;
  const supportsLastFrame = supportsLastFrameOf(caps);
  const referencesOwned = references !== undefined && !!onReferencesChange;
  // Declared by the registry, not guessed: Veo's reference mode rejects a
  // request that also carries an anchor frame, and the provider's 400 is a
  // wasted generation. The UI enforces it by CLEARING rather than disabling,
  // so either choice is always one click away.
  const framesExcludeReferences =
    referencesOwned && excluded(caps, 'frame_images', 'reference_images');
  const referenceCount = references ? countReferenceInputs(references) : 0;

  const setSlot = (slot: FrameSlot, binding: FrameBinding | null) => {
    if (binding && framesExcludeReferences && referenceCount > 0) {
      onReferencesChange!(emptyReferenceInputs());
    }
    if (slot === 'first') {
      onChange({ ...state, first: binding });
      return;
    }
    onChange({ ...state, last: binding, lastEnabled: binding !== null });
  };

  const handleReferencesChange = (next: ReferenceInputState) => {
    const grew = countReferenceInputs(next) > referenceCount;
    if (grew && framesExcludeReferences && (state.first || state.last)) {
      onChange({ first: null, last: null, lastEnabled: false });
    }
    onReferencesChange!(next);
  };

  const firstLabel = supportsFrameImages ? 'First frame' : 'Reference image';

  return (
    <div className="space-y-2 pt-2 border-t border-white/10">
      <div className="flex items-baseline justify-between">
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-300 font-medium">
          Frames used for this generation
        </p>
        {!capabilityKnown && (
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">
            checking model…
          </span>
        )}
      </div>

      <div className="flex items-start gap-3">
        <FrameSlotCard
          slot="first"
          label={firstLabel}
          binding={state.first}
          dimmed={false}
          candidates={candidates}
          onPick={(binding) => setSlot('first', binding)}
          onBrowse={() => setPickerSlot('first')}
          onRegenerateImage={onRegenerateImage}
          busyMediaId={busyMediaId}
          refreshToken={refreshToken}
        />

        {supportsLastFrame && (
          <button
            type="button"
            onClick={() => onChange({ ...state, lastEnabled: !state.lastEnabled })}
            aria-pressed={state.lastEnabled}
            data-testid="chain-toggle"
            className="self-center shrink-0 w-16 flex flex-col items-center gap-1 group"
          >
            <span
              aria-hidden="true"
              className={`block w-full border-t ${
                state.lastEnabled
                  ? 'border-white/60'
                  : 'border-dashed border-white/15 group-hover:border-white/40'
              }`}
            />
            <span
              className={`font-mono text-[10px] uppercase tracking-[0.12em] ${
                state.lastEnabled ? 'text-zinc-300' : 'text-zinc-600 group-hover:text-zinc-400'
              }`}
            >
              {state.lastEnabled ? 'chained' : 'unchained'}
            </span>
          </button>
        )}

        {supportsLastFrame && (
          <FrameSlotCard
            slot="last"
            label="Last frame"
            binding={state.last}
            dimmed={!state.lastEnabled}
            candidates={candidates}
            onPick={(binding) => setSlot('last', binding)}
            onBrowse={() => setPickerSlot('last')}
            onRegenerateImage={onRegenerateImage}
            busyMediaId={busyMediaId}
            refreshToken={refreshToken}
          />
        )}
      </div>

      {referencesOwned && (
        <ReferenceInputsEditor
          state={references!}
          onChange={handleReferencesChange}
          caps={caps}
          exclusiveWithFrames={framesExcludeReferences}
        />
      )}

      <p className="font-mono text-[10px] leading-relaxed text-zinc-600">
        {capabilityKnown && !supportsFrameImages
          ? 'This model takes a single reference image, so there is no closing frame to chain.'
          : 'Regenerating a reference image replaces it in place — the video picks up the new still.'}
      </p>

      {pickerSlot && (
        <MediaLibraryPickerModal
          isOpen={!!pickerSlot}
          onClose={() => setPickerSlot(null)}
          filterType="image"
          onSelect={(selected: MediaLibraryItem) => {
            const url = (selected.url || selected.preview_url || '').trim();
            if (url) setSlot(pickerSlot, { url, mediaId: selected.media_id || selected.id });
            setPickerSlot(null);
          }}
        />
      )}
    </div>
  );
};

export default ReferenceFramesEditor;
